import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HelmetProvider } from 'react-helmet-async';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { useDarkMode } from './hooks/useDarkMode';
import { useUserMode } from './hooks/useUserMode';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useMonitorWalletsSync } from './hooks/useMonitorWalletsSync';
import { useAnalyticsPageView } from './hooks/useAnalyticsPageView';
import { useDeviceSync } from './hooks/useDeviceSync';
import { useAuthStore } from './store/authStore';
import { supabase } from './lib/supabase/client';
import { StorageService } from './services/storage';
import { migrationService } from './services/migrationService';
import { LogService } from './services/logService';
import { alertService } from './services/alertService';
// import { voiceCommands } from './services/voiceCommands'; // Temporarily disabled
import { biometricAuth } from './services/biometricAuth';
import { pinService } from './services/pinService';
import { badgeService } from './services/badgeService';
import { BTCPriceHistoryService } from './services/btcPriceHistory';
import { useCoinsList } from './hooks/queries/useCoinGecko';
import { CoinGeckoAPI } from './services/api';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AppShell } from './components/layout/AppShell';
import { CommandPalette, type Command } from './components/ui/CommandPalette';
import { OnboardingModal } from './components/onboarding/OnboardingModal';
import { WhatsNewModal } from './components/WhatsNewModal';
import { PWAUpdatePrompt } from './components/pwa/PWAUpdatePrompt';
// import { VoiceButton } from './components/voice/VoiceButton'; // Temporarily disabled
import { BiometricSetup } from './components/auth/BiometricSetup';
import { PINSetup } from './components/auth/PINSetup';
import { LockScreen } from './components/auth/LockScreen';
import { AlertPanel } from './components/alerts/AlertPanel';
import { SyncChoiceModal } from './components/SyncChoiceModal';
import { ConflictResolutionModal } from './components/ConflictResolutionModal';
import { SupportChat } from './components/support';
import type { DataComparison } from './services/migrationService';
import { SEO, getPageSEO } from './components/SEO';
import { PortfolioDashboard } from './pages/PortfolioDashboard';
import { Bot } from './pages/Bot';
import { Academy } from './pages/Academy';
import { AcademyV2 } from './pages/AcademyV2';
import { CryptoQuiz } from './pages/CryptoQuiz';
import { MyLearning } from './pages/MyLearning';
import { Documentation } from './pages/Documentation';
import { GettingStarted } from './pages/GettingStarted';
import { CryptoComRoadmap } from './pages/CryptoComRoadmap';
import { ComprehensiveGuide } from './pages/ComprehensiveGuide';
import { WalletTracker } from './pages/WalletTracker';
import { CongressTrading } from './pages/CongressTrading';
import { CryptoComDashboard } from './pages/CryptoComDashboard';
import { KucoinDashboard } from './pages/KucoinDashboard';
import { Logs } from './pages/Logs';
import { Changelog } from './pages/Changelog';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TaxStrategies } from './pages/TaxStrategies';
import { TaxCenter } from './pages/TaxCenter';
import Autopilot from './pages/Autopilot';
import { Settings } from './pages/Settings';
import { MyAccount } from './pages/MyAccount';
import { PublicAPI } from './pages/PublicAPI';
import { TaxLossHarvesting } from './pages/TaxLossHarvesting';
import { PortfolioRebalancing } from './pages/PortfolioRebalancing';
// V2.G8: AnalyticsHub aggregates the 6 analytics pages behind one
// MonitorTabs row. The standalone Insights import is no longer used
// directly by App.tsx — it's lazy-loaded by AnalyticsHub itself.
import { AnalyticsHub, type AnalyticsTabId } from './pages/analytics/AnalyticsHub';
import { SeedCrypt } from './pages/SeedCrypt';
import BitcoinCycles from './pages/BitcoinCycles';
import BitcoinRetirement from './pages/BitcoinRetirement';
import { FluxNodeMonitor } from './pages/FluxNodeMonitor';
import { HederaMonitor } from './pages/HederaMonitor';
import CronosMonitor from './pages/CronosMonitor';
import NetworksCompare from './pages/NetworksCompare';
import { TheGraphMonitor } from './pages/TheGraphMonitor';
import { AlgorandMonitor } from './pages/AlgorandMonitor';
import { CardanoMonitor } from './pages/CardanoMonitor';
import { XdcMonitor } from './pages/XdcMonitor';
import { VechainMonitor } from './pages/VechainMonitor';
import { IcpMonitor } from './pages/IcpMonitor';
import { AxelarMonitor } from './pages/AxelarMonitor';
import { AIConsensusBot } from './pages/AIConsensusBot';
import { SupportAdmin } from './pages/SupportAdmin';
import { HostingAdmin } from './pages/HostingAdmin';
import { AdminDashboard } from './pages/AdminDashboard';
import { ModuleHub } from './pages/ModuleHub';
import { PageModulesProvider } from './hooks/usePageModules';
import { Projects } from './pages/Projects';
import { Recommendations } from './pages/Recommendations';
import { SwapAnalyticsPage } from './pages/SwapAnalyticsPage';
import { HomePage } from './pages/HomePage';
import { DashboardIpad } from './pages/DashboardIpad';
import { PortfolioOnChain } from './pages/PortfolioOnChain';
import { Pricing } from './pages/Pricing';
import { Advisor } from './pages/Advisor';
import { Blog } from './pages/Blog';
import { OpenClawOnFlux } from './pages/OpenClawOnFlux';
import { AlertsPage } from './pages/AlertsPage';
// V2.G8: the 6 analytics pages are no longer rendered directly here.
// AnalyticsHub (lazy-loads them) is the sole consumer.
import { Icons } from './components/Icons';
import { VERSION_INFO } from './config/version';
import { queryClient } from './config/queryClient';
import { CelebrationProvider } from './components/micro-interactions/CelebrationProvider';
import { FeatureDiscoveryProvider } from './contexts/FeatureDiscoveryContext';
import { MicroInteractionsDemo } from './pages/MicroInteractionsDemo';
import type { CalculationMethod, Token, Coin } from './types';

// Tab ID type - all valid navigation tabs
type TabId =
  | 'home' | 'portfolio' | 'alerts' | 'settings' | 'account'
  | 'bitcoin-cycles' | 'bitcoin-retirement' | 'bot' | 'autopilot' | 'academy' | 'crypto-quiz' | 'my-learning' | 'guide'
  | 'tax-strategies' | 'tax-center' | 'wallet-tracker' | 'cryptocom-dashboard'
  | 'kucoin-dashboard' | 'changelog' | 'logs' | 'tax-loss-harvesting'
  | 'portfolio-rebalancing' | 'seed-vault' | 'fluxnode-monitor'
  | 'hedera-monitor' | 'cronos-monitor' | 'thegraph-monitor' | 'algorand-monitor' | 'cardano-monitor'
  | 'xdc-monitor' | 'vechain-monitor' | 'icp-monitor' | 'axelar-monitor' | 'networks-compare' | 'module-hub' | 'projects' | 'recommendations'
  | 'swap-analytics' | 'analytics-overview' | 'analytics-performance' | 'analytics-risk'
  | 'analytics-trading' | 'analytics-positions' | 'analytics-insights' | 'micro-demo' | 'public-api'
  | 'ai-consensus' | 'support-admin' | 'hosting-admin' | 'admin-dashboard' | 'portfolio-onchain' | 'pricing'
  | 'dashboard-ipad' | 'advisor' | 'blog' | 'congress-trading' | 'openclaw-on-flux' | 'privacy';

// Tab to URL mapping (Option B: URL sync)
const TAB_TO_URL: Record<TabId, string> = {
  'home': '/',
  'portfolio': '/portfolio',
  'alerts': '/alerts',
  'settings': '/settings',
  'account': '/account',
  'bitcoin-cycles': '/bitcoin-cycles',
  'bitcoin-retirement': '/bitcoin-retirement',
  'bot': '/bot',
  'autopilot': '/autopilot',
  'academy': '/academy',
  'crypto-quiz': '/quiz',
  'my-learning': '/my-learning',
  'guide': '/guide',
  'tax-strategies': '/tax-strategies',
  'tax-center': '/tax-center',
  'wallet-tracker': '/wallet-tracker',
  'cryptocom-dashboard': '/cryptocom-dashboard',
  'kucoin-dashboard': '/kucoin-dashboard',
  'changelog': '/changelog',
  'logs': '/logs',
  'tax-loss-harvesting': '/tax-loss-harvesting',
  'portfolio-rebalancing': '/portfolio-rebalancing',
  'seed-vault': '/seedcrypt',
  'fluxnode-monitor': '/fluxnode-monitor',
  'hedera-monitor': '/hedera-monitor',
  'cronos-monitor': '/cronos-monitor',
  'thegraph-monitor': '/thegraph-monitor',
  'algorand-monitor': '/algorand-monitor',
  'cardano-monitor': '/cardano-monitor',
  'xdc-monitor': '/xdc-monitor',
  'vechain-monitor': '/vechain-monitor',
  'icp-monitor': '/icp-monitor',
  'axelar-monitor': '/axelar-monitor',
  'networks-compare': '/networks/compare',
  'module-hub': '/modules',
  'projects': '/projects',
  'recommendations': '/recommendations',
  'swap-analytics': '/swap-analytics',
  'analytics-overview': '/analytics/overview',
  'analytics-performance': '/analytics/performance',
  'analytics-risk': '/analytics/risk',
  'analytics-trading': '/analytics/trading',
  'analytics-positions': '/analytics/positions',
  'analytics-insights': '/analytics/insights',
  'micro-demo': '/demo',
  'public-api': '/public-api',
  'ai-consensus': '/ai-consensus',
  'support-admin': '/support-admin',
  'hosting-admin': '/hosting-admin',
  'admin-dashboard': '/admin-dashboard',
  'portfolio-onchain': '/portfolio-onchain',
  'pricing': '/pricing',
  'dashboard-ipad': '/dashboard-ipad',
  'advisor': '/advisor',
  'blog': '/blog',
  'congress-trading': '/congress-trading',
  'openclaw-on-flux': '/openclaw-on-flux',
  'privacy': '/privacy',
};

// URL to Tab mapping (reverse)
const URL_TO_TAB: Record<string, string> = Object.fromEntries(
  Object.entries(TAB_TO_URL).map(([tab, url]) => [url, tab])
);

/**
 * Legacy URL → new URL redirect map (V2.G5).
 *
 * Visualizations 1/2/3 were deleted in V2.G5; their charts have been
 * canonicalized into the analytics/* pages per V2.G1 decisions. Old
 * bookmarks land on a sensible analytics destination via a one-shot
 * `navigate(replace)` in the URL sync effect.
 */
const LEGACY_URL_REDIRECTS: Record<string, string> = {
  '/visualizations': '/analytics/overview',
  '/visualizations2': '/analytics/performance',
  '/visualizations3': '/analytics/positions',
  '/advanced-analytics': '/analytics/insights',
};

// Ordered list of pages for Cmd+Up/Down navigation
const NAVIGABLE_PAGES = [
  'home',
  'portfolio',
  'alerts',
  'bitcoin-cycles',
  'bitcoin-retirement',
  'bot',
  'autopilot',
  'academy',
  'crypto-quiz',
  'my-learning',
  'guide',
  'tax-strategies',
  'tax-center',
  'wallet-tracker',
  'cryptocom-dashboard',
  'kucoin-dashboard',
  'fluxnode-monitor',
  'hedera-monitor',
  'thegraph-monitor',
  'algorand-monitor',
  'cardano-monitor',
  'xdc-monitor',
  'vechain-monitor',
  'icp-monitor',
  'axelar-monitor',
  'networks-compare',
  'tax-loss-harvesting',
  'portfolio-rebalancing',
  'seed-vault',
  'analytics-overview',
  'analytics-performance',
  'analytics-risk',
  'analytics-trading',
  'analytics-positions',
  'analytics-insights',
  'swap-analytics',
  'public-api',
  'ai-consensus',
  'recommendations',
  'changelog',
  'logs',
  'module-hub',
  'settings',
  'account',
  'congress-trading',
];

// Inner component that has access to React Query
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isDark, toggleDark] = useDarkMode();
  const [userMode, setUserMode] = useUserMode();
  const { loadUser } = useAuthStore();

  // Sync monitor wallets from cloud after login (FluxNode, Cronos, Hedera)
  // This ensures widgets display data without visiting each monitor page first
  useMonitorWalletsSync();

  // V2.M.2 — auto-emit page_view events on every route change.
  useAnalyticsPageView();

  // V2.N.4 — mirror local data (wallets, portfolios, pages) to DB,
  // even for anonymous users. Async, non-blocking.
  useDeviceSync();

  // Track if navigation is from URL change (browser back/forward) to avoid replacing history
  const isNavigatingFromUrl = useRef(false);
  // Track last navigated path to prevent duplicate navigations
  const lastNavigatedPath = useRef(location.pathname);

  // Initialize activeTab from URL on mount (Option B: read URL)
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const tabFromUrl = URL_TO_TAB[location.pathname];
    return (tabFromUrl as TabId) || 'home';
  });
  const [calculationMethod, setCalculationMethod] = useState<CalculationMethod>(() => {
    return StorageService.get<CalculationMethod>('calculation_method', 'FIFO');
  });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [showPINSetup, setShowPINSetup] = useState(false);
  const [showAlertPanel, setShowAlertPanel] = useState(false);
  const [showSyncChoice, setShowSyncChoice] = useState(false);
  const [syncChoiceUserId, setSyncChoiceUserId] = useState<string | null>(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictComparison, setConflictComparison] = useState<DataComparison | null>(null);
  const [isLocked, setIsLocked] = useState(() => {
    // Check if app should be locked on start (PIN or biometric auth)
    return pinService.isAppLocked();
  });

  // Load ALL available coins for alert selection (not just portfolio tokens)
  const { data: allCoins = [], isLoading: isLoadingCoins } = useCoinsList({ enabled: showAlertPanel });

  // Convert coins to tokens format for AlertPanel
  const tokens: Token[] = useMemo(() => {
    return allCoins.map(coin => ({
      id: coin.id,
      symbol: coin.symbol,
      name: coin.name,
      amount: 0, // Not relevant for alerts
      avgBuyPrice: 0, // Not relevant for alerts
      // Note: Coin type doesn't include image, AlertForm will handle tokens without images
    }));
  }, [allCoins]);

  // Alert panel data - fetch prices when panel opens
  const [currentPrices, setCurrentPrices] = useState<Record<string, { usd: number }>>({});

  // Fetch prices for the most popular coins when alert panel opens
  useEffect(() => {
    if (!showAlertPanel || allCoins.length === 0) return;

    // Fetch prices for top 100 coins (CoinGecko limit)
    const topCoins = allCoins.slice(0, 100);
    const coinIds = topCoins.map(t => t.id);

    if (coinIds.length > 0) {
      CoinGeckoAPI.fetchPrices(coinIds)
        .then(data => {
          const prices: Record<string, { usd: number }> = {};
          Object.keys(data).forEach(id => {
            // Only add if usd price is defined
            if (data[id]?.usd !== undefined) {
              prices[id] = { usd: data[id].usd };
            }
          });
          setCurrentPrices(prices);
        })
        .catch(err => console.error('Error fetching prices for alerts:', err));
    }
  }, [showAlertPanel, allCoins]);

  // Sync URL when activeTab changes (write to URL)
  // This handles programmatic navigation (sidebar clicks, widget navigation, etc.)
  useEffect(() => {
    // Skip entirely if this tab change was triggered by URL change (browser back/forward)
    // This prevents the loop: URL changes → setActiveTab → navigate back → URL changes...
    if (isNavigatingFromUrl.current) {
      isNavigatingFromUrl.current = false;
      return;
    }

    const targetUrl = TAB_TO_URL[activeTab];

    // Skip if no URL mapping or URL already matches
    if (!targetUrl || location.pathname === targetUrl) {
      return;
    }

    // Skip if we just navigated to this path (prevents duplicate navigations)
    if (lastNavigatedPath.current === targetUrl) {
      return;
    }

    // User-initiated navigation: push to history
    lastNavigatedPath.current = targetUrl;
    navigate(targetUrl);
  }, [activeTab, navigate]); // Removed location.pathname - only react to activeTab changes

  // Sync activeTab when URL changes (read from URL - for browser back/forward)
  // This handles browser back/forward button navigation
  useEffect(() => {
    // V2.G5: legacy /visualizations* URLs redirect to canonical analytics/*.
    const legacyTarget = LEGACY_URL_REDIRECTS[location.pathname];
    if (legacyTarget) {
      navigate(legacyTarget, { replace: true });
      return;
    }

    const tabFromUrl = URL_TO_TAB[location.pathname];

    // Skip if no tab mapping for this URL or tab already matches
    if (!tabFromUrl || tabFromUrl === activeTab) {
      return;
    }

    // Update tracking ref to prevent the other effect from navigating back
    lastNavigatedPath.current = location.pathname;

    // Mark that this navigation is from URL change (browser back/forward)
    isNavigatingFromUrl.current = true;
    setActiveTab(tabFromUrl as TabId);
  }, [location.pathname]); // Intentionally not including activeTab to avoid circular updates

  // Initialize auth state on mount and listen for changes
  useEffect(() => {
    let authEventReceived = false;

    // Listen for Supabase auth state changes and sync with authStore
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      authEventReceived = true;
      // Handle all auth events by reloading user state
      loadUser();

      // SYNC/CONFLICT DETECTION: When user signs in, check data state
      if ((event === 'SIGNED_IN') && session?.user?.id) {
        const userId = session.user.id;

        // Sync BTC price history (bidirectional)
        BTCPriceHistoryService.fullSync().catch(err => {
          console.error('[App] Error syncing BTC price history:', err);
        });

        // Check if user has already made a sync choice
        const syncChoiceMade = StorageService.get('sync_choice_made', false);
        const hasLocal = migrationService.hasLocalData();
        const hasCloud = await migrationService.hasCloudData(userId);

        if (!syncChoiceMade && hasLocal) {
          if (hasCloud) {
            // CONFLICT: User has BOTH local and cloud data
            const comparison = await migrationService.compareData(userId);

            if (comparison.hasConflict) {
              setConflictComparison(comparison);
              setSyncChoiceUserId(userId);
              setShowConflictModal(true);
            } else {
              StorageService.set('sync_choice_made', true);
            }
          } else {
            // No cloud data - show first-time sync modal
            setSyncChoiceUserId(userId);
            setShowSyncChoice(true);
          }
        }
      }
    });

    // Fallback: If onAuthStateChange doesn't fire within 500ms, call loadUser manually
    // This handles edge cases where Supabase events might not fire properly
    const fallbackTimeout = setTimeout(() => {
      if (!authEventReceived) {
        loadUser();
      }
    }, 500);

    return () => {
      subscription.unsubscribe();
      clearTimeout(fallbackTimeout);
    };
  }, [loadUser]);

  // Initialize LogService and Analytics on mount
  useEffect(() => {
    LogService.initialize({
      maxLogs: 1000,
      captureConsole: true,
      captureErrors: true,
    });

    // Initialize alert service
    alertService.init();

    // Initialize PIN service
    const pinInitialized = pinService.init();
    if (pinInitialized && pinService.isAppLocked()) {
      setIsLocked(true);
    }

    // Initialize biometric auth (optional enhancement on top of PIN)
    biometricAuth.init();

    // Initialize badge service
    badgeService.init();

    // Check if user has completed onboarding
    const onboardingCompleted = StorageService.get('onboarding_completed', false);
    if (!onboardingCompleted) {
      // Delay onboarding modal to let app render first
      setTimeout(() => {
        setShowOnboarding(true);
      }, 1000);
    }

    // Cleanup on unmount
    return () => {
      alertService.destroy();
      // voiceCommands.destroy(); // Temporarily disabled
    };
  }, [toggleDark]);

  const pageNames = {
    home: 'Home Dashboard',
    portfolio: 'Portfolio Dashboard',
    bot: 'AI Trading Bot',
    autopilot: 'Autopilot',
    academy: 'Edouard.ai Academy',
    'crypto-quiz': 'Quiz Crypto',
    'my-learning': 'My Learning Dashboard',
    guide: 'Guide Complet',
    'tax-strategies': 'Tax Strategies',
    'tax-center': 'Tax Center',
    'wallet-tracker': 'Wallet Tracker',
    'congress-trading': 'Congress Trading',
    'cryptocom-dashboard': 'Crypto.com Dashboard',
    'kucoin-dashboard': 'KuCoin Dashboard',
    changelog: 'Changelog',
    logs: 'System Logs',
    'tax-loss-harvesting': 'Tax Loss Harvesting',
    'portfolio-rebalancing': 'Portfolio Rebalancing',
    'fluxnode-monitor': 'FluxNode Monitor',
    'hedera-monitor': 'Hedera Monitor',
    'thegraph-monitor': 'The Graph Monitor',
    'algorand-monitor': 'Algorand Monitor',
    'cardano-monitor': 'Cardano Monitor',
    'xdc-monitor': 'XDC Monitor',
    'vechain-monitor': 'VeChain Monitor',
    'icp-monitor': 'ICP Monitor',
    'axelar-monitor': 'Axelar Monitor',
    'module-hub': 'Module Hub',
    // New Analytics Pages
    'analytics-overview': 'Vue d\'Ensemble',
    'analytics-performance': 'Analyse de Performance',
    'analytics-risk': 'Analyse de Risque',
    'analytics-trading': 'Outils de Trading',
    'analytics-positions': 'Gestion des Positions',
    'analytics-insights': 'Insights',
    settings: 'Paramètres'
  };

  // Save calculation method to storage when it changes
  const handleCalculationMethodChange = (method: CalculationMethod) => {
    setCalculationMethod(method);
    StorageService.set('calculation_method', method);
  };

  // Handle add token from onboarding
  const handleAddToken = () => {
    setActiveTab('portfolio');
    // Note: This will trigger add token modal in PortfolioDashboard
    // The actual implementation will be handled by PortfolioDashboard component
  };

  // Define commands for the command palette
  const commands: Command[] = useMemo(() => [
    // Navigation
    {
      id: 'nav-home',
      label: 'Go to Home Dashboard',
      icon: Icons.Home,
      category: 'Navigation',
      keywords: ['home', 'dashboard', 'overview', 'accueil'],
      onExecute: () => setActiveTab('home')
    },
    {
      id: 'nav-portfolio',
      label: 'Go to Portfolio',
      icon: Icons.Briefcase,
      category: 'Navigation',
      keywords: ['portfolio', 'tokens', 'dashboard'],
      onExecute: () => setActiveTab('portfolio')
    },
    {
      id: 'nav-bot',
      label: 'Go to AI Trading Bot',
      icon: Icons.Sparkles,
      category: 'Navigation',
      keywords: ['bot', 'ai', 'trading', 'signals', 'automated', 'advisor', 'recommendations'],
      onExecute: () => setActiveTab('bot')
    },
    {
      id: 'nav-autopilot',
      label: 'Go to Autopilot',
      icon: Icons.Sparkles,
      category: 'Navigation',
      keywords: ['autopilot', 'trading', 'ai', 'automated', 'signals', 'binance'],
      onExecute: () => setActiveTab('autopilot')
    },
    {
      id: 'nav-academy',
      label: 'Go to Edouard.ai Academy',
      icon: Icons.BookOpen,
      category: 'Navigation',
      keywords: ['academy', 'learn', 'education', 'courses', 'training', 'tutorials', 'videos', 'achievements'],
      onExecute: () => setActiveTab('academy')
    },
    {
      id: 'nav-crypto-quiz',
      label: 'Go to Quiz Crypto',
      icon: Icons.Brain,
      category: 'Navigation',
      keywords: ['quiz', 'crypto', 'test', 'knowledge', 'questions', 'qcm', 'apprentissage', 'xp'],
      onExecute: () => setActiveTab('crypto-quiz')
    },
    {
      id: 'nav-my-learning',
      label: 'Go to My Learning Dashboard',
      icon: Icons.User,
      category: 'Navigation',
      keywords: ['learning', 'dashboard', 'progress', 'stats', 'analytics', 'my', 'achievements', 'goals'],
      onExecute: () => setActiveTab('my-learning')
    },
    {
      id: 'nav-guide',
      label: 'Go to Complete Guide',
      icon: Icons.FileText,
      category: 'Navigation',
      keywords: ['guide', 'documentation', 'docs', 'help', 'getting started', 'tutorial', 'onboarding', 'roadmap', 'features', 'faq'],
      onExecute: () => setActiveTab('guide')
    },
    {
      id: 'nav-tax-strategies',
      label: 'Go to Tax Strategies',
      icon: Icons.BookOpen,
      category: 'Navigation',
      keywords: ['tax', 'fiscal', 'impôts', 'lombard', 'aave', 'card', 'carte', 'stratégie', 'fiscalité', 'flat tax', 'éviter', 'optimisation'],
      onExecute: () => setActiveTab('tax-strategies')
    },
    {
      id: 'nav-tax-center',
      label: 'Go to Tax Center',
      icon: Icons.Calculator,
      category: 'Navigation',
      keywords: ['tax', 'center', 'fiscal', 'impôts', 'déclaration', 'formulaire', '2086', '3916', 'plus-value', 'france', 'calcul', 'import', 'csv', 'waltio'],
      onExecute: () => setActiveTab('tax-center')
    },
    {
      id: 'nav-wallet-tracker',
      label: 'Go to Wallet Tracker',
      icon: Icons.Wallet,
      category: 'Navigation',
      keywords: ['wallet', 'tracker', 'smart money', 'whale', 'watch', 'follow', 'ethereum', 'transactions', 'tracking'],
      onExecute: () => setActiveTab('wallet-tracker')
    },
    {
      id: 'nav-congress-trading',
      label: 'Go to Congress Trading',
      icon: Icons.Briefcase,
      category: 'Navigation',
      keywords: ['congress', 'trading', 'politician', 'pelosi', 'stock', 'senate', 'house', 'disclosure', 'insider'],
      onExecute: () => setActiveTab('congress-trading')
    },
    {
      id: 'nav-cryptocom-dashboard',
      label: 'Go to Crypto.com Dashboard',
      icon: Icons.Activity,
      category: 'Navigation',
      keywords: ['cryptocom', 'crypto.com', 'dashboard', 'bots', 'trading', 'staking', 'card', 'cro'],
      onExecute: () => setActiveTab('cryptocom-dashboard')
    },
    {
      id: 'nav-kucoin-dashboard',
      label: 'Go to KuCoin Dashboard',
      icon: Icons.KuCoin,
      category: 'Navigation',
      keywords: ['kucoin', 'dashboard', 'bots', 'trading', 'earn', 'vip'],
      onExecute: () => setActiveTab('kucoin-dashboard')
    },
    {
      id: 'nav-changelog',
      label: 'Go to Changelog',
      icon: Icons.FileText,
      category: 'Navigation',
      keywords: ['changelog', 'history', 'versions', 'releases', 'updates', 'what\'s new'],
      onExecute: () => setActiveTab('changelog')
    },
    {
      id: 'nav-logs',
      label: 'Go to System Logs',
      icon: Icons.FileText,
      category: 'Navigation',
      keywords: ['logs', 'system', 'debug', 'console'],
      onExecute: () => setActiveTab('logs')
    },
    {
      id: 'nav-fluxnode-monitor',
      label: 'Go to FluxNode Monitor',
      icon: Icons.Server,
      category: 'Navigation',
      keywords: ['fluxnode', 'flux', 'node', 'monitor', 'zelcore', 'wallet', 'rewards', 'mining'],
      onExecute: () => setActiveTab('fluxnode-monitor')
    },
    {
      id: 'nav-hedera-monitor',
      label: 'Go to Hedera Monitor',
      icon: Icons.Wallet,
      category: 'Navigation',
      keywords: ['hedera', 'hbar', 'hashgraph', 'staking', 'rewards', 'hashpack', 'wallet', 'tokens', 'nft'],
      onExecute: () => setActiveTab('hedera-monitor')
    },
    {
      id: 'nav-thegraph-monitor',
      label: 'Go to The Graph Monitor',
      icon: Icons.Layers,
      category: 'Navigation',
      keywords: ['thegraph', 'graph', 'grt', 'indexer', 'delegator', 'delegation', 'staking', 'rewards', 'subgraph', 'arbitrum'],
      onExecute: () => setActiveTab('thegraph-monitor')
    },
    // V2.I.11 : pages indexées en complément (audit cross-app a montré
    // que ces destinations n'étaient pas accessibles via Cmd+K).
    {
      id: 'nav-alerts',
      label: 'Go to Alerts',
      icon: Icons.Bell,
      category: 'Navigation',
      keywords: ['alerts', 'alertes', 'price', 'notification', 'watch', 'trigger'],
      onExecute: () => setActiveTab('alerts' as any)
    },
    {
      id: 'nav-account',
      label: 'Go to My Account',
      icon: Icons.User,
      category: 'Navigation',
      keywords: ['account', 'compte', 'profile', 'profil', 'security', 'sécurité', 'subscriptions', 'abonnements', 'biometric', 'pin'],
      onExecute: () => setActiveTab('account' as any)
    },
    {
      id: 'nav-ai-consensus',
      label: 'Go to AI Consensus Bot',
      icon: Icons.Sparkles,
      category: 'Navigation',
      keywords: ['ai', 'consensus', 'bot', 'multi-model', 'gpt', 'claude', 'grok', 'analysis'],
      onExecute: () => setActiveTab('ai-consensus' as any)
    },
    {
      id: 'nav-bitcoin-cycles',
      label: 'Go to Bitcoin Cycles',
      icon: Icons.Activity,
      category: 'Bitcoin',
      keywords: ['bitcoin', 'btc', 'cycles', 'halving', 'pi cycle', 'mvrv', 'rainbow', 'indicator'],
      onExecute: () => setActiveTab('bitcoin-cycles' as any)
    },
    {
      id: 'nav-bitcoin-retirement',
      label: 'Go to Bitcoin Retirement',
      icon: Icons.Briefcase,
      category: 'Bitcoin',
      keywords: ['bitcoin', 'btc', 'retirement', 'retraite', 'plan', 'calculator', 'simulation'],
      onExecute: () => setActiveTab('bitcoin-retirement' as any)
    },
    {
      id: 'nav-token-risk',
      label: 'Go to Token Risk Analysis',
      icon: Icons.Shield,
      category: 'Bitcoin',
      keywords: ['token', 'risk', 'risque', 'ath', 'min2y', 'analysis', 'volatility'],
      onExecute: () => setActiveTab('token-risk-analysis' as any)
    },
    // Chain monitors (Cardano, Algorand, Vechain, Xdc, Cronos, Icp, Axelar)
    {
      id: 'nav-cardano-monitor',
      label: 'Go to Cardano Monitor',
      icon: Icons.Wallet,
      category: 'Chain Monitors',
      keywords: ['cardano', 'ada', 'monitor', 'staking', 'pool', 'delegation'],
      onExecute: () => setActiveTab('cardano-monitor' as any)
    },
    {
      id: 'nav-algorand-monitor',
      label: 'Go to Algorand Monitor',
      icon: Icons.Wallet,
      category: 'Chain Monitors',
      keywords: ['algorand', 'algo', 'monitor', 'governance', 'staking'],
      onExecute: () => setActiveTab('algorand-monitor' as any)
    },
    {
      id: 'nav-vechain-monitor',
      label: 'Go to VeChain Monitor',
      icon: Icons.Wallet,
      category: 'Chain Monitors',
      keywords: ['vechain', 'vet', 'vtho', 'monitor', 'thor'],
      onExecute: () => setActiveTab('vechain-monitor' as any)
    },
    {
      id: 'nav-xdc-monitor',
      label: 'Go to XDC Monitor',
      icon: Icons.Wallet,
      category: 'Chain Monitors',
      keywords: ['xdc', 'xinfin', 'monitor', 'staking'],
      onExecute: () => setActiveTab('xdc-monitor' as any)
    },
    {
      id: 'nav-cronos-monitor',
      label: 'Go to Cronos Monitor',
      icon: Icons.Wallet,
      category: 'Chain Monitors',
      keywords: ['cronos', 'cro', 'crypto.com', 'monitor', 'staking'],
      onExecute: () => setActiveTab('cronos-monitor' as any)
    },
    {
      id: 'nav-icp-monitor',
      label: 'Go to ICP Monitor',
      icon: Icons.Wallet,
      category: 'Chain Monitors',
      keywords: ['icp', 'internet computer', 'dfinity', 'monitor', 'staking', 'neuron'],
      onExecute: () => setActiveTab('icp-monitor' as any)
    },
    {
      id: 'nav-axelar-monitor',
      label: 'Go to Axelar Monitor',
      icon: Icons.Wallet,
      category: 'Chain Monitors',
      keywords: ['axelar', 'axl', 'monitor', 'cross-chain', 'bridge', 'gmp'],
      onExecute: () => setActiveTab('axelar-monitor' as any)
    },
    {
      id: 'nav-module-hub',
      label: 'Go to Module Hub',
      icon: Icons.Layout,
      category: 'Navigation',
      keywords: ['modules', 'hub', 'configuration', 'pages', 'activer', 'désactiver', 'enable', 'disable', 'settings', 'personnaliser'],
      onExecute: () => setActiveTab('module-hub')
    },
    {
      id: 'nav-pricing',
      label: 'Go to Pricing',
      icon: Icons.CreditCard,
      category: 'Navigation',
      keywords: ['pricing', 'tarifs', 'prix', 'abonnement', 'subscription', 'plan', 'premium', 'pro', 'expert'],
      onExecute: () => setActiveTab('pricing')
    },
    // New Analytics Pages
    {
      id: 'nav-analytics-overview',
      label: 'Go to Portfolio Overview',
      icon: Icons.PieChart,
      category: 'Navigation',
      keywords: ['analytics', 'overview', 'vue', 'ensemble', 'portfolio', 'dashboard', 'kpi'],
      onExecute: () => setActiveTab('analytics-overview')
    },
    {
      id: 'nav-analytics-performance',
      label: 'Go to Performance Analytics',
      icon: Icons.TrendingUp,
      category: 'Navigation',
      keywords: ['analytics', 'performance', 'chart', 'drawdown', 'calendar', 'heatmap'],
      onExecute: () => setActiveTab('analytics-performance')
    },
    {
      id: 'nav-analytics-risk',
      label: 'Go to Risk Analysis',
      icon: Icons.Shield,
      category: 'Navigation',
      keywords: ['analytics', 'risk', 'risque', 'correlation', 'prediction', 'matrix'],
      onExecute: () => setActiveTab('analytics-risk')
    },
    {
      id: 'nav-analytics-trading',
      label: 'Go to Trading Tools',
      icon: Icons.Activity,
      category: 'Navigation',
      keywords: ['analytics', 'trading', 'tools', 'volume', 'liquidity', 'technical', 'sentiment'],
      onExecute: () => setActiveTab('analytics-trading')
    },
    {
      id: 'nav-analytics-positions',
      label: 'Go to Position Management',
      icon: Icons.Target,
      category: 'Navigation',
      keywords: ['analytics', 'positions', 'management', 'target', 'rebalancing', 'holding'],
      onExecute: () => setActiveTab('analytics-positions')
    },
    {
      id: 'nav-analytics-insights',
      label: 'Go to Insights',
      icon: Icons.Activity,
      category: 'Navigation',
      keywords: ['analytics', 'insights', 'sharpe', 'drawdown', 'health', 'suggestions', 'metrics'],
      onExecute: () => setActiveTab('analytics-insights')
    },
    {
      id: 'nav-swap-analytics',
      label: 'Go to Swap Analytics',
      icon: Icons.ArrowRightLeft,
      category: 'Navigation',
      keywords: ['swap', 'analytics', 'exchange', 'trade', 'history', 'export', 'tax', 'bridge', 'cross-chain'],
      onExecute: () => setActiveTab('swap-analytics')
    },
    {
      id: 'nav-settings',
      label: 'Go to Settings',
      icon: Icons.Settings,
      category: 'Navigation',
      keywords: ['settings', 'preferences', 'config', 'configuration', 'paramètres'],
      onExecute: () => setActiveTab('settings')
    },
    // Theme
    {
      id: 'toggle-theme',
      label: isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode',
      icon: isDark ? Icons.Sun : Icons.Moon,
      shortcut: 'T',
      category: 'Preferences',
      keywords: ['theme', 'dark', 'light', 'mode'],
      onExecute: () => toggleDark()
    },
    // Calculation Methods
    {
      id: 'calc-fifo',
      label: 'Use FIFO Method',
      icon: Icons.Calculator,
      category: 'Calculation',
      keywords: ['fifo', 'first', 'in', 'out', 'calculation'],
      onExecute: () => handleCalculationMethodChange('FIFO')
    },
    {
      id: 'calc-lifo',
      label: 'Use LIFO Method',
      icon: Icons.Calculator,
      category: 'Calculation',
      keywords: ['lifo', 'last', 'in', 'out', 'calculation'],
      onExecute: () => handleCalculationMethodChange('LIFO')
    },
    {
      id: 'calc-average',
      label: 'Use Average Cost Method',
      icon: Icons.Calculator,
      category: 'Calculation',
      keywords: ['average', 'cost', 'mean', 'calculation'],
      onExecute: () => handleCalculationMethodChange('AVERAGE_COST')
    },
    // Actions (these would be implemented in PortfolioDashboard, just placeholders for now)
    {
      id: 'add-token',
      label: 'Add Token',
      icon: Icons.Plus,
      shortcut: 'A',
      category: 'Actions',
      keywords: ['add', 'new', 'token', 'coin', 'crypto'],
      onExecute: () => {
        setActiveTab('portfolio');
        // TODO: Trigger add token modal
      }
    },
    {
      id: 'export-csv',
      label: 'Export to CSV',
      icon: Icons.Download,
      shortcut: 'Ctrl+S',
      category: 'Export',
      keywords: ['export', 'download', 'csv', 'save'],
      onExecute: () => {
        // TODO: Implement CSV export
      }
    },
    {
      id: 'export-json',
      label: 'Export to JSON',
      icon: Icons.Save,
      shortcut: 'Ctrl+E',
      category: 'Export',
      keywords: ['export', 'download', 'json', 'backup'],
      onExecute: () => {
        // TODO: Implement JSON export
      }
    },
    {
      id: 'import-json',
      label: 'Import from JSON',
      icon: Icons.Upload,
      shortcut: 'Ctrl+I',
      category: 'Import',
      keywords: ['import', 'upload', 'json', 'restore'],
      onExecute: () => {
        // TODO: Implement JSON import
      }
    },
    // Security
    {
      id: 'biometric-setup',
      label: 'Biometric Authentication',
      icon: Icons.Fingerprint,
      category: 'Security',
      keywords: ['biometric', 'fingerprint', 'face', 'id', 'touch', 'security', 'lock'],
      onExecute: () => {
        setShowBiometricSetup(true);
      }
    },
  ], [isDark, toggleDark]);

  // Render current page
  const renderPage = () => {
    switch (activeTab) {
      case 'home':
        return <HomePage calculationMethod={calculationMethod} onNavigate={(pageId) => setActiveTab(pageId as TabId)} />;
      case 'portfolio':
        return <PortfolioDashboard calculationMethod={calculationMethod} userMode={userMode} onNavigate={(pageId) => setActiveTab(pageId as TabId)} />;
      case 'alerts':
        return <AlertsPage onNavigate={(pageId) => setActiveTab(pageId as TabId)} />;
      case 'bitcoin-cycles':
        return <BitcoinCycles />;
      case 'bitcoin-retirement':
        return <BitcoinRetirement />;
      case 'bot':
        return <Bot />;
      case 'autopilot':
        return <Autopilot />;
      case 'academy':
        return <AcademyV2 />;
      case 'crypto-quiz':
        return <CryptoQuiz />;
      case 'my-learning':
        return <MyLearning />;
      case 'guide':
        return <ComprehensiveGuide />;
      case 'tax-strategies':
        return <TaxStrategies />;
      case 'tax-center':
        return <TaxCenter userMode={userMode} />;
      case 'wallet-tracker':
        return <WalletTracker />;
      case 'congress-trading':
        return <CongressTrading />;
      case 'cryptocom-dashboard':
        return <CryptoComDashboard />;
      case 'kucoin-dashboard':
        return <KucoinDashboard />;
      case 'changelog':
        return <Changelog />;
      case 'privacy':
        return <PrivacyPolicy />;
      case 'logs':
        return <Logs />;
      case 'tax-loss-harvesting':
        return <TaxLossHarvesting />;
      case 'portfolio-rebalancing':
        return <PortfolioRebalancing />;
      case 'seed-vault':
        return <SeedCrypt />;
      case 'fluxnode-monitor':
        return <FluxNodeMonitor userMode={userMode} />;
      case 'hedera-monitor':
        return <HederaMonitor />;
      case 'cronos-monitor':
        return <CronosMonitor />;
      case 'thegraph-monitor':
        return <TheGraphMonitor />;
      case 'algorand-monitor':
        return <AlgorandMonitor />;
      case 'cardano-monitor':
        return <CardanoMonitor />;
      case 'xdc-monitor':
        return <XdcMonitor />;
      case 'vechain-monitor':
        return <VechainMonitor />;
      case 'icp-monitor':
        return <IcpMonitor />;
      case 'axelar-monitor':
        return <AxelarMonitor />;
      case 'networks-compare':
        return <NetworksCompare onNavigate={(pageId) => setActiveTab(pageId as TabId)} />;
      case 'portfolio-onchain':
        return <PortfolioOnChain userMode={userMode} />;
      case 'pricing':
        return <Pricing />;
      case 'module-hub':
        return (
          <ModuleHub
            userMode={userMode}
            onNavigate={(pageId) => setActiveTab(pageId as TabId)}
            isDarkMode={isDark}
            onToggleDarkMode={toggleDark}
            onToggleUserMode={() => setUserMode(userMode === 'simple' ? 'expert' : 'simple')}
          />
        );
      case 'projects':
        return <Projects />;
      case 'recommendations':
        return <Recommendations />;
      // V2.G8: All 6 analytics destinations route through AnalyticsHub,
      // which renders MonitorTabs at the top + dispatches to the right
      // child page. The standalone TabIds survive (bookmarks unchanged).
      case 'analytics-overview':
      case 'analytics-performance':
      case 'analytics-risk':
      case 'analytics-trading':
      case 'analytics-positions':
      case 'analytics-insights':
        return (
          <AnalyticsHub
            activeTab={activeTab as AnalyticsTabId}
            onTabChange={(id) => setActiveTab(id)}
          />
        );
      case 'swap-analytics':
        return <SwapAnalyticsPage userMode={userMode} />;
      case 'public-api':
        return <PublicAPI onNavigateToSettings={() => setActiveTab('settings')} />;
      case 'ai-consensus':
        return <AIConsensusBot />;
      case 'advisor':
        return <Advisor />;
      case 'micro-demo':
        return <MicroInteractionsDemo />;
      case 'support-admin':
        return <SupportAdmin />;
      case 'hosting-admin':
        return <HostingAdmin />;
      case 'admin-dashboard':
        return <AdminDashboard />;
      case 'openclaw-on-flux':
        return <OpenClawOnFlux onNavigate={(pageId) => setActiveTab(pageId as TabId)} />;
      case 'blog':
        return <Blog />;
      case 'settings':
        return <Settings
          onNavigateToAPI={() => {
            setActiveTab('public-api');
          }}
          onNavigate={(pageId) => setActiveTab(pageId as TabId)}
        />;
      case 'account':
        return <MyAccount
          onNavigate={(pageId) => setActiveTab(pageId as TabId)}
          onOpenPINSetup={() => setShowPINSetup(true)}
          onOpenBiometricSetup={() => setShowBiometricSetup(true)}
        />;
      case 'dashboard-ipad':
        return <DashboardIpad />;
      default:
        return <HomePage calculationMethod={calculationMethod} onNavigate={(pageId) => setActiveTab(pageId as TabId)} />;
    }
  };

  // Handle biometric unlock
  const handleUnlock = () => {
    setIsLocked(false);
  };

  // Page navigation with Cmd/Ctrl + Arrow keys
  const navigateToPreviousPage = useCallback(() => {
    const currentIndex = NAVIGABLE_PAGES.indexOf(activeTab);
    if (currentIndex > 0) {
      const previousPage = NAVIGABLE_PAGES[currentIndex - 1];
      setActiveTab(previousPage as TabId);
    }
  }, [activeTab]);

  const navigateToNextPage = useCallback(() => {
    const currentIndex = NAVIGABLE_PAGES.indexOf(activeTab);
    if (currentIndex < NAVIGABLE_PAGES.length - 1) {
      const nextPage = NAVIGABLE_PAGES[currentIndex + 1];
      setActiveTab(nextPage as TabId);
    }
  }, [activeTab]);

  // Global keyboard shortcuts for page navigation (Cmd/Ctrl + ArrowUp/ArrowDown)
  useKeyboardShortcuts([
    {
      key: 'ArrowUp',
      ctrl: true, // ctrl: true also handles metaKey (Cmd) on Mac
      action: navigateToPreviousPage,
      description: 'Go to previous page'
    },
    {
      key: 'ArrowDown',
      ctrl: true,
      action: navigateToNextPage,
      description: 'Go to next page'
    }
  ]);

  // Get current page SEO config
  const currentPageSEO = getPageSEO(activeTab);

  return (
    <>
      {/* SEO Meta Tags */}
      <SEO {...currentPageSEO} />

      {/* Lock Screen - Renders above everything if locked */}
      {isLocked && <LockScreen onUnlock={handleUnlock} />}

      <ErrorBoundary>
        <AppShell
          activeTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as TabId)}
          currentPage={pageNames[activeTab] || activeTab}
          calculationMethod={calculationMethod}
          onCalculationMethodChange={handleCalculationMethodChange}
          onShowAlertPanel={() => setShowAlertPanel(true)}
          userMode={userMode}
          onUserModeChange={setUserMode}
        >
          {/* Content */}
          {renderPage()}

          {/* Footer */}
          <footer style={{
            marginTop: '40px',
            paddingTop: '20px',
            borderTop: '1px solid var(--border)',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '13px',
              color: 'var(--text)',
              fontWeight: 500,
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px'
            }}>
              <span>Edouard.ai - Swiss-Army-Knife Crypto Toolkit</span>
              <span style={{ color: 'var(--muted)' }}>•</span>
              <span
                onClick={() => setActiveTab('changelog')}
                style={{
                  color: 'var(--primary)',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.opacity = '0.7'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.opacity = '1'}
              >
                📋 Changelog
              </span>
            </div>
            <div style={{
              fontSize: '11px',
              color: 'var(--muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{
                  background: 'var(--primary)',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: 600
                }}>
                  v{VERSION_INFO.version}
                </span>
              </span>
              <span>•</span>
              <span>{VERSION_INFO.phase}</span>
              <span>•</span>
              <span>
                {VERSION_INFO.currentTask}: {VERSION_INFO.taskName}
                <span style={{
                  marginLeft: '4px',
                  color: VERSION_INFO.taskStatus === 'Completed' ? '#10b981' : '#f59e0b',
                  fontWeight: 600
                }}>
                  ({VERSION_INFO.taskStatus})
                </span>
              </span>
              <span>•</span>
              <span>Build: {VERSION_INFO.buildDate}</span>
              <span>•</span>
              <span
                onClick={() => setActiveTab('public-api')}
                style={{
                  color: 'var(--primary)',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  transition: 'opacity 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.opacity = '0.7'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.opacity = '1'}
              >
                🔌 API
              </span>
            </div>
            <div style={{
              fontSize: '10px',
              color: 'var(--muted)',
              marginTop: '6px',
              opacity: 0.6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <span>© 2025 Edouard.ai • {VERSION_INFO.environment.toUpperCase()}</span>
              {VERSION_INFO.commitHash !== 'local' && VERSION_INFO.commitHash !== 'dev' && <span>• {VERSION_INFO.commitHash}</span>}
              <span>•</span>
              <a
                href="https://ko-fi.com/edouardai"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#ef4444',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.opacity = '0.7'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.opacity = '1'}
              >
                <Icons.Heart size={12} /> Support
              </a>
            </div>
            <div style={{
              fontSize: '10px',
              color: 'var(--muted)',
              marginTop: '8px',
              opacity: 0.7,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}>
              <span>made by</span>
              <a
                href="https://x.com/swissmnco"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'var(--primary)',
                  textDecoration: 'none',
                  fontWeight: 500,
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.opacity = '0.7'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.opacity = '1'}
              >
                @swissmnco
              </a>
              <span>to communities — feel free to ask any features!</span>
            </div>
          </footer>
        </AppShell>

        {/* Command Palette */}
        <CommandPalette commands={commands} />

        {/* Onboarding Modal */}
        {showOnboarding && (
          <OnboardingModal
            onComplete={() => setShowOnboarding(false)}
            onAddToken={handleAddToken}
          />
        )}

        {/* What's New Modal - Shows new features after update */}
        {/* Disabled when onboarding is showing to prevent both modals appearing */}
        <WhatsNewModal
          disabled={showOnboarding}
          onNavigate={(path) => {
            const tab = (URL_TO_TAB[path] || 'home') as TabId;
            setActiveTab(tab);
          }}
        />

        {/* PWA Update Prompt - Shows when new version available */}
        <PWAUpdatePrompt />

        {/* PIN Setup Modal */}
        <PINSetup
          isOpen={showPINSetup}
          onClose={() => setShowPINSetup(false)}
          onSuccess={() => {}}
        />

        {/* Biometric Setup Modal */}
        <BiometricSetup
          isOpen={showBiometricSetup}
          onClose={() => setShowBiometricSetup(false)}
          onSuccess={() => {}}
        />

        {/* Alert Panel - Notifications only */}
        <AlertPanel
          isOpen={showAlertPanel}
          onClose={() => setShowAlertPanel(false)}
          onNavigateToAlerts={() => {
            setShowAlertPanel(false);
            setActiveTab('alerts');
          }}
        />

        {/* Sync Choice Modal - shown when user signs in with local data (no cloud) */}
        {showSyncChoice && syncChoiceUserId && (
          <SyncChoiceModal
            isOpen={showSyncChoice}
            userId={syncChoiceUserId}
            onComplete={() => {
              setShowSyncChoice(false);
              setSyncChoiceUserId(null);
            }}
          />
        )}

        {/* Conflict Resolution Modal - shown when user has BOTH local and cloud data */}
        {showConflictModal && syncChoiceUserId && conflictComparison && (
          <ConflictResolutionModal
            isOpen={showConflictModal}
            userId={syncChoiceUserId}
            comparison={conflictComparison}
            onComplete={() => {
              setShowConflictModal(false);
              setSyncChoiceUserId(null);
              setConflictComparison(null);
            }}
          />
        )}
      </ErrorBoundary>

      {/* Voice Commands Button - Temporarily disabled */}
      {/* <VoiceButton /> */}

      {/* React Query DevTools - Only in development */}
      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      )}
    </>
  );
}

// Main App wrapper that provides React Query context, PageModules context, Celebrations, Feature Discovery and SEO
function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <FeatureDiscoveryProvider>
          <CelebrationProvider>
            <PageModulesProvider>
              <AppContent />
              <SupportChat />
            </PageModulesProvider>
          </CelebrationProvider>
        </FeatureDiscoveryProvider>
      </QueryClientProvider>
      {/* Vercel Analytics & Speed Insights */}
      <Analytics />
      <SpeedInsights />
    </HelmetProvider>
  );
}

export default App;
